import React, { useEffect, useRef, useState } from "react";
import { DateTime } from "luxon";
import { McModal } from "@maersk-global/mds-react-wrapper/components-core/mc-modal";
import { McInput } from "@maersk-global/mds-react-wrapper/components-core/mc-input";
import { McTypeahead } from "@maersk-global/mds-react-wrapper/components-core/mc-typeahead";
import { McTextarea } from "@maersk-global/mds-react-wrapper/components-core/mc-textarea";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import {
  fetchAppDetailsByAppId,
  fetchAppDetailsByAppName,
} from "../../RedGreenChannelTracker/apiHelpers";

const getDefaultFormState = () => ({
  app_id: "",
  app_name: "",
  platform: "",
  portfolio: "",
  reason_for_restriction: "",
  added_to_change_restriction: DateTime.now().toFormat("dd-MM-yyyy"),
});

export default function AddChangeRestrictionModal({
  isOpen,
  isSubmitting,
  graphData,
  isEditMode = false,
  initialData = null,
  onClose,
  onSubmit,
}) {
  const [formData, setFormData] = useState(getDefaultFormState);
  const [appNameOptions, setAppNameOptions] = useState([]);
  const [validationError, setValidationError] = useState("");
  const appIdTimerRef = useRef(null);
  const appNameTimerRef = useRef(null);

  const addedBy = graphData?.displayName || "";

  const resolveInputDate = (value) => {
    if (!value) {
      return DateTime.now().toFormat("dd-MM-yyyy");
    }

    const tryIso = DateTime.fromISO(String(value));
    if (tryIso.isValid) {
      return tryIso.toFormat("dd-MM-yyyy");
    }

    const tryDdMm = DateTime.fromFormat(String(value), "dd-MM-yyyy");
    if (tryDdMm.isValid) {
      return tryDdMm.toFormat("dd-MM-yyyy");
    }

    const tryDdMmm = DateTime.fromFormat(String(value), "dd-MMM-yyyy");
    if (tryDdMmm.isValid) {
      return tryDdMmm.toFormat("dd-MM-yyyy");
    }

    return DateTime.now().toFormat("dd-MM-yyyy");
  };

  const getFormStateFromRecord = (record) => ({
    app_id: record?.app_id || "",
    app_name: record?.app_name || "",
    platform: record?.platform || "",
    portfolio: record?.portfolio || "",
    reason_for_restriction: record?.reason_for_restriction || record?.reason || "",
    added_to_change_restriction: resolveInputDate(
      record?.added_to_change_restriction ||
        record?.added_on ||
        record?.created_at ||
        record?.created_date,
    ),
  });

  useEffect(() => {
    if (!isOpen) {
      setFormData(getDefaultFormState());
      setAppNameOptions([]);
      setValidationError("");
      return;
    }

    if (isEditMode && initialData) {
      setFormData(getFormStateFromRecord(initialData));
    } else {
      setFormData(getDefaultFormState());
    }
    setAppNameOptions([]);
    setValidationError("");
  }, [isOpen, isEditMode, initialData]);

  useEffect(() => {
    return () => {
      if (appIdTimerRef.current) {
        clearTimeout(appIdTimerRef.current);
      }
      if (appNameTimerRef.current) {
        clearTimeout(appNameTimerRef.current);
      }
    };
  }, []);

  const setField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAppIdInput = (value) => {
    setField("app_id", value);
    if (!value || !value.trim()) {
      setField("platform", "");
      setField("portfolio", "");
      setAppNameOptions([]);
      return;
    }

    setAppNameOptions([]);

    if (appIdTimerRef.current) {
      clearTimeout(appIdTimerRef.current);
    }

    appIdTimerRef.current = setTimeout(async () => {
      const data = await fetchAppDetailsByAppId(value.trim());
      if (!data) {
        return;
      }

      setFormData((prev) => ({
        ...prev,
        platform: data.platform || "",
        portfolio: data.portfolio || "",
      }));

      if (Array.isArray(data.app_names) && data.app_names.length > 0) {
        setAppNameOptions(
          data.app_names.map((name) => ({ label: name, value: name })),
        );
      }
    }, 800);
  };

  const handleAppNameInput = (value) => {
    setField("app_name", value);

    if (appNameTimerRef.current) {
      clearTimeout(appNameTimerRef.current);
    }

    appNameTimerRef.current = setTimeout(async () => {
      if (!value || !value.trim()) {
        return;
      }

      const data = await fetchAppDetailsByAppName(value.trim());
      if (!data) {
        return;
      }

      setFormData((prev) => ({
        ...prev,
        app_id: data.app_id || prev.app_id,
        platform: data.platform || "",
        portfolio: data.portfolio || "",
      }));
    }, 800);
  };

  const handleAppNameSelected = (event) => {
    const selectedName = event.detail?.label || event.detail;
    setField("app_name", selectedName);
  };

  const handleSubmit = () => {
    const requiredFields = [];

    if (!formData.app_id || !String(formData.app_id).trim()) {
      requiredFields.push("Application ID");
    }
    if (!formData.app_name || !String(formData.app_name).trim()) {
      requiredFields.push("App Instance Name");
    }
    if (!formData.platform || !String(formData.platform).trim()) {
      requiredFields.push("Platform");
    }
    if (!formData.portfolio || !String(formData.portfolio).trim()) {
      requiredFields.push("Portfolio");
    }
    if (requiredFields.length > 0) {
      setValidationError(requiredFields.join(", "));
      return;
    }

    const parsedDate = DateTime.fromFormat(
      formData.added_to_change_restriction,
      "dd-MM-yyyy",
    );
    if (!parsedDate.isValid) {
      setValidationError("Added to Change Restriction must be in DD-MM-YYYY format");
      return;
    }

    setValidationError("");

    onSubmit({
      ...formData,
      added_by: isEditMode ? (initialData?.added_by || initialData?.created_by || addedBy) : addedBy,
      added_to_change_restriction: parsedDate.toFormat("yyyy-MM-dd HH:mm:ss"),
    });
  };

  return (
    <McModal
      heading={isEditMode ? "Edit Change Restriction" : "Add to Change Restriction"}
      dimension="large"
      padding="default"
      open={isOpen}
      closed={onClose}
      closing={onClose}
      zindex={999}
    >
      <div className="change-restriction-form-grid">
        <div className="change-restriction-form-field">
          <label className="change-restriction-form-label">
            Application ID <span className="required-star">*</span>
          </label>
          <McInput
            hiddenlabel={true}
            value={formData.app_id}
            input={(event) => handleAppIdInput(event.target.value)}
            placeholder="Enter Application ID"
            fit="medium"
          />
        </div>

        <div className="change-restriction-form-field">
          <label className="change-restriction-form-label">
            App Instance Name <span className="required-star">*</span>
          </label>
          <McTypeahead
            hiddenlabel={true}
            value={formData.app_name}
            data={appNameOptions}
            input={(event) => handleAppNameInput(event.target.value)}
            optionselected={handleAppNameSelected}
            placeholder="Enter or select App Instance Name"
            showlistonfocus={true}
            clearbutton={true}
            maxoptions="10"
            fit="medium"
            nosuggestions="Enter App ID/Name to autofill related details"
          />
        </div>

        <div className="change-restriction-form-field">
          <label className="change-restriction-form-label">
            Platform <span className="required-star">*</span>
          </label>
          <McInput
            hiddenlabel={true}
            value={formData.platform}
            input={(event) => setField("platform", event.target.value)}
            placeholder="Auto-filled"
            fit="medium"
          />
        </div>

        <div className="change-restriction-form-field">
          <label className="change-restriction-form-label">
            Portfolio <span className="required-star">*</span>
          </label>
          <McInput
            hiddenlabel={true}
            value={formData.portfolio}
            input={(event) => setField("portfolio", event.target.value)}
            placeholder="Auto-filled"
            fit="medium"
          />
        </div>

        <div className="change-restriction-form-field">
          <label className="change-restriction-form-label">
            Added by<span className="required-star">*</span>
          </label>
          <McInput hiddenlabel={true} value={addedBy} disabled fit="medium" />
        </div>

        <div className="change-restriction-form-field">
          <label className="change-restriction-form-label">
            Added to Change Restriction<span className="required-star">*</span>
          </label>
          <McInput
            hiddenlabel={true}
            value={formData.added_to_change_restriction}
            input={(event) =>
              setField("added_to_change_restriction", event.target.value)
            }
            placeholder="DD-MM-YYYY"
            fit="medium"
          />
        </div>

        <div className="change-restriction-form-field full-width">
          <label className="change-restriction-form-label">
            Reason for restriction
          </label>
          <McTextarea
            hiddenlabel={true}
            value={formData.reason_for_restriction}
            input={(event) =>
              setField("reason_for_restriction", event.target.value)
            }
            placeholder="Enter reason for restriction"
            rows="4"
            fit="medium"
          />
        </div>
      </div>

      {validationError && (
        <div className="change-restriction-form-error">
          <strong>Please fill in mandatory fields:</strong> {validationError}
        </div>
      )}

      <McButton
        slot="secondaryAction"
        appearance="neutral"
        variant="outlined"
        dialogaction="cancel"
        fit="medium"
        disabled={isSubmitting}
        click={onClose}
      >
        Cancel
      </McButton>
      <McButton
        slot="primaryAction"
        appearance="primary"
        fit="medium"
        loading={isSubmitting}
        disabled={isSubmitting}
        click={handleSubmit}
      >
        {isEditMode ? "Save" : "Add"}
      </McButton>
    </McModal>
  );
}
